import type { Subscription } from "@/context/SubscriptionContext";
import { analyzeSubscriptionStack } from "@/lib/insight-engine";

export type RenewalRiskLevel = "safe" | "watch" | "act-soon" | "critical";
export type RuleTone = "info" | "warning" | "danger" | "success";

export type RenewalRiskScore = {
  id: string;
  name: string;
  category: string;
  score: number;
  level: RenewalRiskLevel;
  daysUntilRenewal: number | null;
  monthlyCost: number;
  annualExposure: number;
  reason: string;
};

export type StackOverlapPlay = {
  id: string;
  category: string;
  services: string[];
  monthlyCost: number;
  suggestedAction: string;
  estimatedAnnualSavings: number;
};

export type SmartNotificationRule = {
  id: string;
  title: string;
  description: string;
  tone: RuleTone;
  enabled: boolean;
};

export type SecurityCenterItem = {
  id: string;
  title: string;
  description: string;
  status: "protected" | "review" | "action";
};

export type PwaReadinessItem = {
  id: string;
  title: string;
  description: string;
  ready: boolean;
};

export type Orizonte12Pulse = {
  releaseName: "Orizonte 1.2";
  headline: string;
  launchPillars: Array<{ id: string; title: string; description: string }>;
  topRenewalRisks: RenewalRiskScore[];
  overlapPlays: StackOverlapPlay[];
  smartRules: SmartNotificationRule[];
  securityCenter: SecurityCenterItem[];
  pwaReadiness: PwaReadinessItem[];
  intelligenceActs: Array<{ id: string; title: string; description: string; action: string }>;
};

const toCurrency = (value: number) => `$${value.toFixed(2)}`;

const levelFromScore = (score: number): RenewalRiskLevel => {
  if (score >= 82) return "critical";
  if (score >= 60) return "act-soon";
  if (score >= 42) return "watch";
  return "safe";
};

const riskReason = (risk: RenewalRiskScore) => {
  if (risk.level === "critical") {
    return `${risk.name} needs a decision now: ${risk.daysUntilRenewal ?? "unknown"} days to renewal with ${toCurrency(risk.annualExposure)}/yr exposure.`;
  }
  if (risk.level === "act-soon") {
    return `${risk.name} is close enough to review before it becomes an automatic charge.`;
  }
  if (risk.level === "watch") {
    return `${risk.name} is stable, but Orizonte should keep it on the renewal radar.`;
  }
  return `${risk.name} has a low renewal pressure signal.`;
};

export const buildOrizonte12Pulse = (
  subscriptions: Subscription[],
  smartRuleOverrides: Record<string, boolean> = {},
): Orizonte12Pulse => {
  const analysis = analyzeSubscriptionStack(subscriptions);

  const topRenewalRisks = analysis.subscriptions
    .map((item) => {
      const score = Math.round(
        item.renewalRisk * 0.5 +
        item.priorityScore * 0.25 +
        item.regretScore * 0.15 +
        (item.trialDanger ? 10 : 0)
      );
      const level = levelFromScore(score);
      const risk: RenewalRiskScore = {
        id: item.id,
        name: item.name,
        category: item.inferredCategory,
        score,
        level,
        daysUntilRenewal: item.daysUntilRenewal,
        monthlyCost: item.monthlyCost,
        annualExposure: item.annualCost,
        reason: "",
      };
      return { ...risk, reason: riskReason(risk) };
    })
    .sort((a, b) => b.score - a.score || b.annualExposure - a.annualExposure)
    .slice(0, 5);

  const overlapPlays = analysis.overlaps.slice(0, 4).map((cluster) => ({
    id: `overlap-${cluster.category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
    category: cluster.category,
    services: cluster.services.map((service) => service.name),
    monthlyCost: cluster.monthlyCost,
    suggestedAction: cluster.suggestedTarget
      ? `Pressure-test ${cluster.suggestedTarget.name} first.`
      : `Choose one primary ${cluster.category} tool.`,
    estimatedAnnualSavings: Number(((cluster.suggestedTarget?.monthlyCost || cluster.monthlyCost * 0.25) * 12).toFixed(2)),
  }));

  const criticalRenewals = topRenewalRisks.filter((risk) => risk.level === "critical" || risk.level === "act-soon");
  const trialCount = subscriptions.filter((sub) => sub.trialEndsAt || /trial/i.test(`${sub.name} ${sub.plan}`)).length;

  const smartRules: SmartNotificationRule[] = [
    {
      id: "annual-renewal-window",
      title: "Annual renewal warnings",
      description: criticalRenewals.length
        ? `${criticalRenewals.length} urgent renewal${criticalRenewals.length === 1 ? "" : "s"} should notify before checkout.`
        : "Notify 30, 7, and 3 days before high-value renewals.",
      tone: criticalRenewals.length ? "danger" : "info",
      enabled: smartRuleOverrides["annual-renewal-window"] ?? true,
    },
    {
      id: "trial-conversion-guard",
      title: "Trial conversion guard",
      description: trialCount ? `${trialCount} trial${trialCount === 1 ? "" : "s"} should warn before converting.` : "Warn when a trial is close to becoming paid.",
      tone: trialCount ? "warning" : "info",
      enabled: smartRuleOverrides["trial-conversion-guard"] ?? true,
    },
    {
      id: "overlap-detection-alert",
      title: "Overlap detector",
      description: overlapPlays.length
        ? `${overlapPlays.length} stack overlap${overlapPlays.length === 1 ? "" : "s"} can become consolidation plays.`
        : "Watch for duplicate tools in the same work category.",
      tone: overlapPlays.length ? "warning" : "success",
      enabled: smartRuleOverrides["overlap-detection-alert"] ?? true,
    },
    {
      id: "monthly-spend-threshold",
      title: "Spend threshold",
      description: `${toCurrency(analysis.totalMonthlySpend)}/mo tracked. Notify if the stack jumps 15% in one cycle.`,
      tone: analysis.totalMonthlySpend > 250 ? "warning" : "info",
      enabled: smartRuleOverrides["monthly-spend-threshold"] ?? true,
    },
  ];

  const securityCenter: SecurityCenterItem[] = [
    {
      id: "clerk-auth",
      title: "Clerk authentication",
      description: "Identity, passwords, sessions, and MFA are delegated to Clerk.",
      status: "protected",
    },
    {
      id: "api-token-verification",
      title: "API token verification",
      description: "Backend requests require Clerk bearer tokens before reading user data.",
      status: "protected",
    },
    {
      id: "data-export-delete",
      title: "Export and delete controls",
      description: "Users can download subscription records and clear account subscription data.",
      status: "protected",
    },
    {
      id: "session-review",
      title: "Session review",
      description: "Open Clerk security center to review devices, passwords, and connected providers.",
      status: "review",
    },
  ];

  const pwaReadiness: PwaReadinessItem[] = [
    {
      id: "manifest",
      title: "Install manifest",
      description: "App name, icons, display mode, and shortcuts are configured.",
      ready: true,
    },
    {
      id: "service-worker",
      title: "Offline shell",
      description: "The service worker caches the app shell and static assets.",
      ready: true,
    },
    {
      id: "mobile-nav",
      title: "Mobile command bar",
      description: "Protected routes expose bottom navigation and AI access on mobile.",
      ready: true,
    },
    {
      id: "install-prompt",
      title: "Install prompt",
      description: "Mobile users get Add to Home Screen guidance when not installed.",
      ready: true,
    },
  ];

  const intelligenceActs = [
    topRenewalRisks[0] && {
      id: "act-renewal",
      title: `Review ${topRenewalRisks[0].name}`,
      description: topRenewalRisks[0].reason,
      action: "Open cancellation readiness",
    },
    overlapPlays[0] && {
      id: "act-overlap",
      title: `${overlapPlays[0].category} consolidation`,
      description: `${overlapPlays[0].services.join(" + ")} cost ${toCurrency(overlapPlays[0].monthlyCost)}/mo together.`,
      action: "Compare overlap",
    },
    {
      id: "act-notifications",
      title: "Tune notification rules",
      description: "Keep alerts high-signal: renewals, trials, overlap, and spend jumps.",
      action: "Open settings",
    },
  ].filter(Boolean) as Orizonte12Pulse["intelligenceActs"];

  return {
    releaseName: "Orizonte 1.2",
    headline: "Pulse turns renewals, overlaps, security, and mobile readiness into one live decision layer.",
    launchPillars: [
      { id: "renewal", title: "Renewal risk scores", description: "Ranks upcoming charges by urgency, cost, and regret risk." },
      { id: "overlap", title: "Stack overlap detector", description: "Finds duplicate tools and turns them into consolidation plays." },
      { id: "notifications", title: "Smart notification rules", description: "Keeps alerts focused on renewals, trials, spend jumps, and duplicate tools." },
      { id: "security", title: "Security center", description: "Surfaces auth, session, export, and data-control readiness." },
      { id: "performance", title: "Performance & UX boost", description: "Keeps heavy screens lazy and mobile flows app-like." },
      { id: "acts", title: "Intelligence that acts", description: "Moves from passive insight cards to specific next decisions." },
    ],
    topRenewalRisks,
    overlapPlays,
    smartRules,
    securityCenter,
    pwaReadiness,
    intelligenceActs,
  };
};
